import { useFocusRing } from '@react-aria/focus';
import { usePress, useHover } from '@react-aria/interactions';
import { mergeProps, useObjectRef, useResizeObserver } from '@react-aria/utils';
import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import type {
  ButtonProps as AriaButtonProps,
  TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import {
  Button as AriaButton,
  composeRenderProps,
  Provider,
  TextField,
} from 'react-aria-components';

import { cn, pressScaleTouch } from '@infinex/ui-styles';
import type { UnsafeStyleProps, BaseStyleProps } from '@infinex/ui-styles';
import { toDataAttributes } from '@infinex/ui-utils/dom';

import { Surface, SurfaceContext } from './Surface';

import { TextContext } from '../content';
import { InputContext } from '../field';
import { View } from '../layout';

export type SurfaceStackProps = {
  children: ReactNode;
  className?: string;
} & BaseStyleProps &
  UnsafeStyleProps;

/**
 * Compose a group of related surfaces together, creating a visual hierarchy.
 *
 * [Taurus docs: `SurfaceStack`](https://ui-sb.app.infinex.dev/?path=/docs/components-surfacestack--docs)
 */
export function SurfaceStack({ className, ...otherProps }: SurfaceStackProps) {
  return (
    <View
      {...otherProps}
      className={cn(
        'group/surface-stack grid [--surface-stack-gap:4px]',
        className
      )}
    />
  );
}

// Segment --------------------------------------------------------------------

const commonSegmentClasses = [
  '[&:not(:first-child)]:mt-(--surface-stack-gap)',
  'bg-surface rounded-small min-h-18 min-w-0 p-4 outline-hidden',
  'focus-visible:outline-focusRingInside',
  'first:rounded-t-large last:rounded-b-large',
];

export type SurfaceStackSegmentProps = {
  children: ReactNode;
  className?: string;
} & BaseStyleProps &
  UnsafeStyleProps;

/**
 * A content segment within a stack.
 */
export function SurfaceStackSegment(props: SurfaceStackSegmentProps) {
  const { children, className, ...otherProps } = props;
  return (
    <Surface {...otherProps} className={cn(commonSegmentClasses, className)}>
      {children}
    </Surface>
  );
}

// Button ---------------------------------------------------------------------

export type SurfaceStackButtonSegmentProps = {
  children: ReactNode;
} & Omit<AriaButtonProps, 'children' | 'className' | 'style'> &
  UnsafeStyleProps;

/**
 * A button segment within a stack. Accepts "label" and "description" slots on child `Text` components.
 *
 * @example
 * <SurfaceStackButtonSegment>
 *   <Emblem />
 *   <YStack>
 *     <Text slot="label">USDC</Text>
 *     <Text slot="description">2,013.1397 USDC</Text>
 *   </YStack>
 *   <ChevronRightIcon />
 * </SurfaceStackButtonSegment>
 */
export function SurfaceStackButtonSegment(
  props: SurfaceStackButtonSegmentProps
) {
  const { children, className, style, ...otherProps } = props;
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <AriaButton
      {...otherProps}
      ref={buttonRef}
      className={cn(
        commonSegmentClasses,
        'text-secondary flex items-center gap-4 py-3 text-start',
        'hover:bg-surfaceHover hover:text-default pressed:bg-surfacePress',
        className
      )}
      // apply press scale to touch devices. mouse has acceptable affordance,
      // scale interferes with swap button overlay.
      style={pressScaleTouch(buttonRef, style)}
    >
      <Provider
        values={[
          [
            TextContext,
            {
              slots: {
                label: {
                  className: 'text-emphasis text-base font-semibold truncate',
                },
                description: {
                  className: 'text-sm font-semibold text-secondary truncate',
                },
              },
            },
          ],
        ]}
      >
        {children}
      </Provider>
    </AriaButton>
  );
}

// Join ---------------------------------------------------------------------

const GAP = 4;

export type SurfaceStackPeekProps = {
  children: ReactNode;
} & UnsafeStyleProps;

const defaultRect = { width: 0, height: 0 };
type Rect = typeof defaultRect;

/**
 * A wrapper component, giving the appearance that the provided element "peeks
 * out" from between surfaces within a stack.
 */
export function SurfaceStackPeek(props: SurfaceStackPeekProps) {
  const { children, className, ...otherProps } = props;
  const [rect, setRect] = useState<Rect>(defaultRect);
  const childRef = useRef<HTMLDivElement>(null);

  const onResize = useCallback(() => {
    const el = childRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setRect((current) => {
      if (width === current.width && height === current.height) return current;
      return { width, height };
    });
  }, []);
  useResizeObserver({ ref: childRef, onResize });
  const paddedHeight = rect.height + GAP * 2;
  // scale function for svg paths
  const s = useCallback((n: number) => n * (paddedHeight / 40), [paddedHeight]);
  const pinchTop = paddedHeight / 2 - GAP / 2;
  const pinchBottom = paddedHeight / 2 + GAP / 2;

  return (
    <div className="pointer-events-none relative">
      <div
        {...otherProps}
        className={cn(
          'absolute inset-x-0 top-0 z-10 grid -translate-y-1/2 place-items-center',
          'mt-[calc(var(--surface-stack-gap)/2)]',
          'grid-cols-[1fr_auto_1fr]',
          '[grid-template-areas:"left_target_right"]',
          '*:[grid-area:target]',
          className
        )}
      >
        <div
          role="presentation"
          className="text-inverse h-full bg-current"
          style={{ width: `calc(100% - ${rect.height}px)` }}
        />
        <PinchSvg height={paddedHeight} gridArea="left">
          <path
            d={`M${s(40)} ${s(40)} V0 C${pinchTop} 0 ${s(26)} ${pinchTop} 0 ${pinchTop} V${pinchBottom} C${s(26)} ${pinchBottom} ${s(26)} ${s(40)} ${s(40)} ${s(40)}Z`}
          />
        </PinchSvg>
        <PinchSvg height={paddedHeight} gridArea="right">
          <path
            d={`M0 ${s(40)} V0 C${pinchBottom} 0 ${s(14)} ${pinchTop} ${s(40)} ${pinchTop} V${pinchBottom} C${s(14)} ${pinchBottom} ${s(14)} ${s(40)} 0 ${s(40)}Z`}
          />
        </PinchSvg>
        <div ref={childRef} className="pointer-events-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

type PinchSvgProps = {
  height: number;
  children: ReactNode;
  gridArea: 'left' | 'right';
};

function PinchSvg(props: PinchSvgProps) {
  const { height, children, gridArea } = props;
  const className =
    gridArea === 'left'
      ? 'mr-(--surface-stack-gap) translate-x-1/2 justify-self-end'
      : 'ml-(--surface-stack-gap) -translate-x-1/2 justify-self-start';

  return (
    <svg
      role="presentation"
      aria-hidden="true"
      focusable="false"
      width={height}
      height={height}
      viewBox={`0 0 ${height} ${height}`}
      className={cn('text-inverse -z-1 fill-current', className)}
      style={{ gridArea }}
    >
      {children}
    </svg>
  );
}

// Field ----------------------------------------------------------------------

export type SurfaceStackFieldSegmentProps = Omit<
  AriaTextFieldProps,
  'className' | 'style'
> &
  UnsafeStyleProps;

/**
 * A stylised `TextField` segment within a stack. Expects a child `Input` to be
 * provided.
 *
 * @example
 * <SurfaceStackFieldSegment>
 *   <Input placeholder="$0" aria-label="Move asset amount" />
 * </SurfaceStackFieldSegment>
 * */
export function SurfaceStackFieldSegment(props: SurfaceStackFieldSegmentProps) {
  const { children, className, isDisabled, style, ...otherProps } = props;

  const inputRef = useObjectRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const onPress = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    // bail if this press is on the input itself
    if (document.activeElement === input) return;

    // focus and place the cursor at the end of the input
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [inputRef]);

  const { isFocused, isFocusVisible, focusProps } = useFocusRing();
  const { isHovered, hoverProps } = useHover({ isDisabled });
  const { pressProps, isPressed } = usePress({ isDisabled, onPress });

  return (
    <TextField
      ref={cardRef}
      isDisabled={isDisabled}
      {...mergeProps(hoverProps, pressProps, focusProps, otherProps)}
      {...toDataAttributes(
        { isPressed, isHovered, isFocused, isFocusVisible },
        { includeRacIdentifier: true }
      )}
      className={cn(
        commonSegmentClasses,
        '@container',
        'flex cursor-text flex-col items-center gap-4 py-6',
        'hover:bg-surfaceHover',
        className
      )}
      style={pressScaleTouch(cardRef, style)({ isPressed })}
    >
      {composeRenderProps(children, (content) => (
        <Provider
          values={[
            [SurfaceContext, 1 as const],
            [
              InputContext,
              {
                ref: inputRef,
                className: cn(
                  'placeholder:text-tertiary h-10 w-full min-w-0 flex-1 appearance-none bg-none px-0 outline-hidden transition-colors duration-200',
                  'text-emphasis font-numeric text-center text-3xl font-semibold tabular-nums'
                ),
              },
            ],
          ]}
        >
          {content}
        </Provider>
      ))}
    </TextField>
  );
}
